// useResponsivePageSize — a predictable pagination page size that adapts to the
// breakpoint, not to pixel measurements. A pixel-fit approach (measure card
// height + viewport space) proved flaky: it measured mid-resize / pre-layout
// and under-counted, and on a tall mobile header it left room for ~1 card —
// paginating one at a time. A per-breakpoint size is consistent and fills a
// screen well enough: fewer rows stacked on a phone, a fuller grid on desktop.
import { useEffect, useState } from "react";

const NARROW = "(max-width: 767.98px)"; // matches the 1-column grid breakpoint

// matchMedia is absent in some environments (e.g. jsdom under test); treat
// "unknown" as the desktop (wider) case.
function narrowNow(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(NARROW).matches;
}

export function useResponsivePageSize(opts: {
  mobile: number;
  desktop: number;
}): number {
  const { mobile, desktop } = opts;
  const [size, setSize] = useState(() => (narrowNow() ? mobile : desktop));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia(NARROW);
    const update = (): void => setSize(mq.matches ? mobile : desktop);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [mobile, desktop]);

  return size;
}
